/**
 * ProcurementTrackerDetail — Iter 402.2 · Phase 2.
 *
 * Mirrors the shape (but not the ProjectPickingCard weight) of
 * PickingListDetail so a procurement tracker feels familiar to any
 * user who's used picking. Header + status pill + progress meter,
 * pull-from-supplier-doc button, editable line rows with
 * qty_ordered / qty_delivered / qty_outstanding / per-line ETA,
 * status transitions.
 *
 * Phase 3 will add the GRV modal + delivery flow on top of this.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Boxes,
  Plus,
  FileDown,
  Trash2,
  ClipboardList,
  CheckCircle2,
  AlertTriangle,
  Building2,
  Truck,
  Package,
  ClipboardCheck,
  X,
  Link as LinkIcon,
  Search,
  FileText,
  Save,
  Edit3,
  ScanLine,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import ProjectLinkChip from "../components/shared/ProjectLinkChip";
import ProjectSearchSelect from "../components/shared/ProjectSearchSelect";
import BlurTextarea from "../components/BlurTextarea";
import { EditLineButton, MergedLinesPanel, MergeIntoButton, RescanDialog } from "../components/procurement/LineMergeTools";


// Iter 425.j — moved to /components/shared/ProjectSearchSelect.jsx so
// the tracker list page can share the same searchable combobox.

// Iter 413.4 (Dirk, 2026-02-28) — All available statuses in canonical
// order for the header dropdown that replaces the individual Mark
// partial / Mark received / Cancel buttons.
const STATUS_PILL = {
  draft:     { c: "#647087", l: "Draft" },
  ordered:   { c: "#1FB6C1", l: "Ordered" },
  partial:   { c: "#F59E0B", l: "Partially received" },
  received:  { c: "#10B981", l: "Complete" },
  closed:    { c: "#647087", l: "Closed" },
  cancelled: { c: "#EF4444", l: "Cancelled" },
  paused:    { c: "#A78BFA", l: "Paused" },
};
const STATUS_OPTIONS = [
  { v: "draft",     l: "Draft" },
  { v: "ordered",   l: "Ordered" },
  { v: "partial",   l: "Partially received" },
  { v: "received",  l: "Complete" },
  { v: "paused",    l: "Paused" },
  { v: "cancelled", l: "Cancelled" },
  { v: "closed",    l: "Closed" },
];

// NEXT_STATUS was used by the old inline action buttons — replaced in
// iter 413.4 by the StatusDropdown component. Left the constant
// removed to avoid confusion.


function fmtQty(n) {
  const v = Number(n || 0);
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}


function EditableTrackerName({ tid, value, onSaved }) {
  // Iter 413.4 (Dirk, 2026-02-28) — Click-to-rename tracker header.
  // Persists via PATCH /procurement/trackers/{tid}. Enter commits,
  // Escape cancels. Falls back to the previous value on failure.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value || ""); }, [value]);

  const commit = async () => {
    const next = (draft || "").trim();
    if (!next || next === (value || "").trim()) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch(`/procurement/trackers/${tid}`, { name: next });
      onSaved?.(next);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed.");
      setDraft(value || "");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { setDraft(value || ""); setEditing(false); }
        }}
        disabled={saving}
        className="hv-input text-lg font-bold w-full"
        placeholder="Give this tracker a name…"
        data-testid="procurement-name-input"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="group text-lg font-bold text-white flex items-center gap-1.5 truncate hover:text-[#1FB6C1] transition text-left w-full"
      data-testid="procurement-name-edit"
      title="Click to rename this tracker"
    >
      <span className="truncate">{value}</span>
      <Edit3 className="w-3.5 h-3.5 text-[#647087] group-hover:text-[#1FB6C1] shrink-0" />
    </button>
  );
}


function StatusDropdown({ status, saving, onChange }) {
  // Iter 413.4 — Replaces the individual Mark partial / Mark
  // received / Cancel buttons with a single status dropdown that
  // covers the full lifecycle including "Paused" (a manual state
  // that _rollup_tracker_status won't overwrite).
  const pill = STATUS_PILL[status] || STATUS_PILL.draft;
  return (
    <select
      value={status || "draft"}
      onChange={(e) => onChange(e.target.value)}
      disabled={saving}
      className="text-[11px] uppercase tracking-wider px-2 py-1 rounded border bg-[#0e1420] font-bold focus:outline-none focus:ring-2 focus:ring-offset-0"
      style={{
        borderColor: `${pill.c}66`,
        background: `${pill.c}14`,
        color: pill.c,
      }}
      data-testid="procurement-status-dropdown"
      title="Change the tracker status"
    >
      {STATUS_OPTIONS.map((s) => (
        <option key={s.v} value={s.v} className="bg-[#0e1420] text-[#dde3ec] font-bold">
          {s.l}
        </option>
      ))}
    </select>
  );
}


export default function ProcurementTrackerDetail() {
  const { tid } = useParams();
  // Iter 426.p10 — When arrived via /procurement/{tid}?highlight=<item_id>
  // (deep link from a project's Spend Breakdown), auto-scroll to the
  // matching item and flash-highlight it. The tracker's built-in
  // per-row project_id editor is then one click away, so unlinking
  // a wrongly-allocated line is a 2-second job.
  const [searchParams] = useSearchParams();
  const highlightItemId = searchParams.get("highlight") || null;
  const navigate = useNavigate();
  const [tracker, setTracker] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [supplierDocs, setSupplierDocs] = useState([]);
  const [showPullPicker, setShowPullPicker] = useState(false);
  const [pullingDid, setPullingDid] = useState(null);
  const [saving, setSaving] = useState(false);
  const [grvs, setGrvs] = useState([]);
  const [showGrvModal, setShowGrvModal] = useState(false);
  const [showRescan, setShowRescan] = useState(false);
  // Iter 425.d — Cached projects for this tracker's customer so the
  // per-line & overall project selectors don't re-fetch on every open.
  const [projects, setProjects] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, gRes] = await Promise.all([
        api.get(`/procurement/trackers/${tid}`),
        api.get(`/procurement/trackers/${tid}/grvs`).catch(() => ({ data: { items: [] } })),
      ]);
      setTracker(tRes.data);
      setItems(tRes.data.items || []);
      setGrvs(gRes.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error(e?.response?.data?.detail || "Failed to load tracker.");
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => { load(); }, [load]);

  // Iter 425.d — Fetch projects scoped to the tracker's customer once.
  useEffect(() => {
    (async () => {
      try {
        const params = tracker?.customer_id
          ? { customer_id: tracker.customer_id, limit: 200 }
          : { limit: 100 };
        const { data } = await api.get("/projects", { params });
        const items = Array.isArray(data) ? data : (data?.items || []);
        setProjects(items);
      } catch {
        setProjects([]);
      }
    })();
  }, [tracker?.customer_id]);

  // Iter 409.1 · Load ALL pullable doc types (PO / Sales Order /
  // Quote). Backend supports these interchangeably on the pull
  // endpoint. Fetch all three in parallel and merge; the picker
  // then labels each row with its doc_type badge.
  useEffect(() => {
    if (!tracker?.supplier_id) return;
    (async () => {
      const types = ["purchase_order", "sales_order_received", "quote_received"];
      try {
        const results = await Promise.all(
          types.map((t) =>
            api.get(`/suppliers/${tracker.supplier_id}/documents?doc_type=${t}`)
              .then((r) => r.data?.items || r.data || [])
              .catch(() => []),
          ),
        );
        setSupplierDocs(results.flat());
      } catch {
        /* silent — pull button will surface empty state */
      }
    })();
  }, [tracker?.supplier_id]);

  const totals = useMemo(() => {
    let ordered = 0, delivered = 0, outstanding = 0;
    for (const it of items) {
      ordered += Number(it.qty_ordered || 0);
      delivered += Number(it.qty_delivered || 0);
      outstanding += Number(it.qty_outstanding || 0);
    }
    return { rows: items.length, ordered, delivered, outstanding };
  }, [items]);

  const pct = totals.ordered
    ? Math.min(100, Math.round((totals.delivered / totals.ordered) * 100))
    : 0;

  async function setStatus(next) {
    setSaving(true);
    try {
      await api.patch(`/procurement/trackers/${tid}`, { status: next });
      toast.success(`Status → ${next}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status change failed.");
    } finally {
      setSaving(false);
    }
  }

  async function pullFromDoc(did) {
    setPullingDid(did);
    try {
      const { data } = await api.post(
        `/procurement/trackers/${tid}/pull-from-supplier-doc`,
        { supplier_doc_id: did }
      );
      toast.success(
        `Pulled ${data.added} new line${data.added === 1 ? "" : "s"}` +
          (data.synced ? `, synced ${data.synced}` : "") +
          (data.supplier_doc_number ? ` from ${data.supplier_doc_number}` : "")
      );
      setShowPullPicker(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pull failed.");
    } finally {
      setPullingDid(null);
    }
  }

  async function patchItem(iid, patch) {
    try {
      await api.patch(`/procurement/items/${iid}`, patch);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  }

  async function deleteItem(iid) {
    if (!window.confirm("Remove this line?")) return;
    try {
      await api.delete(`/procurement/items/${iid}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  }

  async function deleteTracker() {
    if (!window.confirm("Archive this tracker? Line items will be hidden.")) return;
    try {
      await api.delete(`/procurement/trackers/${tid}`);
      toast.success("Tracker archived.");
      navigate("/procurement");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-6 flex items-center gap-2 text-[#a0aabf]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading tracker…
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="max-w-5xl mx-auto p-6 text-center">
        <div className="text-lg text-white mb-2">Tracker not found</div>
        <Link to="/procurement" className="hv-btn hv-btn-ghost text-xs">
          <ArrowLeft className="w-3 h-3" /> Back to Procurement
        </Link>
      </div>
    );
  }

  const pill = STATUS_PILL[tracker.status] || STATUS_PILL.draft; // kept for compatibility / other refs
  const eligibleDocs = supplierDocs.filter(
    (d) => (d.line_items || []).length > 0 && d.ocr_status === "ready"
  );

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="procurement-tracker-detail">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4 text-xs text-[#a0aabf]">
        <Link to="/procurement" className="hover:text-white flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Procurement
        </Link>
        <span>/</span>
        <span className="text-[#dde3ec] truncate">{tracker.name || tracker.id}</span>
      </div>

      <div className="hv-card p-4 mb-4">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="p-2 rounded-md bg-[#1FB6C1]/10 border border-[#1FB6C1]/30">
            <Boxes className="w-5 h-5 text-[#1FB6C1]" />
          </div>
          <div className="flex-1 min-w-0">
            {/* Iter 413.4 (Dirk, 2026-02-28) — Inline-editable tracker
                name. Click the name (or the pencil) to rename this
                tracker to something the team can identify at a glance,
                e.g. "WCSA · Booster pump replacement" instead of the
                default "PO tracker · PUR1261". */}
            <EditableTrackerName
              tid={tid}
              value={tracker.name || tracker.id}
              onSaved={(name) => setTracker((t) => ({ ...t, name }))}
            />
            <div className="text-xs text-[#a0aabf] flex items-center gap-2 mt-0.5 flex-wrap">
              <Building2 className="w-3 h-3" />
              <Link
                to={`/suppliers/${tracker.supplier_id}`}
                className="hover:text-[#1FB6C1]"
              >
                {tracker.supplier_name}
              </Link>
              {tracker.default_expected_delivery_date && (
                <>
                  <span>·</span>
                  <Truck className="w-3 h-3" />
                  <span>ETA {tracker.default_expected_delivery_date}</span>
                </>
              )}
              {tracker.delivery_method && (
                <>
                  <span>·</span>
                  <span className="uppercase tracking-wider text-[10px]">
                    {tracker.delivery_method}
                  </span>
                </>
              )}
            </div>
          </div>
          {/* Iter 413.4 — Status is now a dropdown (draft / ordered /
              partially received / complete / paused / cancelled /
              closed) so the header isn't cluttered with 3-4 action
              buttons. Colour follows the selected status. */}
          <StatusDropdown
            status={tracker.status}
            saving={saving}
            onChange={(v) => setStatus(v)}
          />
        </div>

        {/* [Iter 420] Reverse project-link chip. */}
        <div className="mt-2">
          <ProjectLinkChip
            kind="procurement_tracker"
            entityId={tracker.id}
            projectId={tracker.project_id}
            customerId={tracker.customer_id}
            onLinked={() => load()}
            onUnlinked={() => load()}
          />
        </div>

        {/* Progress meter */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] text-[#a0aabf] mb-1">
            <span>
              {totals.rows} rows · ordered {fmtQty(totals.ordered)} · delivered{" "}
              {fmtQty(totals.delivered)} · outstanding {fmtQty(totals.outstanding)}
            </span>
            <span className="font-mono text-[#dde3ec]">{pct}%</span>
          </div>
          <div className="h-1.5 rounded bg-white/5 overflow-hidden">
            <div
              className="h-full bg-[#10B981] transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowPullPicker((v) => !v)}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="procurement-pull-btn"
            disabled={saving}
          >
            <FileDown className="w-3 h-3" /> Pull from Supplier Doc
          </button>
          {(tracker.source_supplier_doc_ids || []).length > 0 && (
            <button
              onClick={() => setShowRescan(true)}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="procurement-rescan-btn"
              disabled={saving}
              title="Read the PO again and review changes before saving"
            >
              <ScanLine className="w-3 h-3" /> Rescan document
            </button>
          )}
          {items.length > 0 && !["closed", "cancelled"].includes(tracker.status) && (
            <button
              onClick={() => setShowGrvModal(true)}
              className="hv-btn hv-btn-primary text-xs"
              style={{ background: "#10B981", borderColor: "#10B981" }}
              data-testid="procurement-receive-btn"
              disabled={saving}
            >
              <Truck className="w-3 h-3" /> Receive stock
            </button>
          )}
          {/* Iter 413.4 — Removed the individual Mark partial /
              Mark received / Cancel action buttons in favour of the
              header status dropdown (see StatusDropdown above). Keeps
              the header uncluttered and lets ops pick "Paused" too. */}
          <button
            onClick={async () => {
              try {
                const resp = await api.get(
                  `/procurement/trackers/${tid}/pdf`,
                  { responseType: "blob", timeout: 60_000 },
                );
                const blob = new Blob([resp.data], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                const cd = resp.headers?.["content-disposition"] || "";
                const m = cd.match(/filename="?([^";]+)"?/i);
                const filename = m
                  ? m[1]
                  : `Procurement_${tracker.short_id || tid.slice(0, 12)}.pdf`;
                const win = window.open(url, "_blank", "noopener,noreferrer");
                if (!win) {
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = filename;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                }
                const pn = resp.headers?.["x-procurement-print-number"];
                toast.success(pn ? `PDF generated (print #${pn}).` : "PDF generated.");
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "PDF failed.");
              }
            }}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="procurement-tracker-pdf-btn"
            title="Open printable PDF"
          >
            <FileText className="w-3 h-3" /> PDF
          </button>
          <div className="flex-1" />
          <button
            onClick={deleteTracker}
            className="hv-btn hv-btn-ghost text-xs text-rose-300"
            data-testid="procurement-tracker-delete"
          >
            <Trash2 className="w-3 h-3" /> Archive
          </button>
        </div>

        {/* Pull picker inline */}
        {showPullPicker && (
          <div
            className="mt-3 border-t border-white/10 pt-3"
            data-testid="procurement-pull-picker"
          >
            <div className="text-xs text-[#a0aabf] mb-2">
              Pick an OCR&apos;d PO, Sales Order or Quote from this supplier:
            </div>
            {eligibleDocs.length === 0 ? (
              <div className="text-xs text-[#647087] p-3 bg-black/20 rounded">
                No OCR&apos;d docs available yet. Upload a PO / Sales Order /
                Quote under{" "}
                <Link
                  to={`/suppliers/${tracker.supplier_id}`}
                  className="text-[#1FB6C1] hover:underline"
                >
                  Supplier Documents
                </Link>{" "}
                and wait for OCR to complete.
              </div>
            ) : (
              <div className="space-y-1">
                {eligibleDocs.map((d) => {
                  const badge = {
                    purchase_order: { l: "PO", c: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30" },
                    sales_order_received: { l: "SO", c: "text-violet-300 bg-violet-500/10 border-violet-500/30" },
                    quote_received: { l: "QT", c: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
                  }[d.doc_type] || { l: (d.doc_type || "?").slice(0, 2).toUpperCase(), c: "text-[#a0aabf] bg-white/5 border-white/10" };
                  return (
                    <button
                      key={d.id}
                      onClick={() => pullFromDoc(d.id)}
                      disabled={!!pullingDid}
                      className="w-full text-left p-2 rounded bg-black/20 hover:bg-black/40 border border-white/5 hover:border-[#1FB6C1]/40 transition text-xs flex items-center gap-2"
                      data-testid={`procurement-pull-doc-${d.id}`}
                    >
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${badge.c} shrink-0`}>
                        {badge.l}
                      </span>
                      <ClipboardList className="w-3.5 h-3.5 text-[#1FB6C1] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">
                          {d.document_number || d.name}
                        </div>
                        <div className="text-[10px] text-[#a0aabf]">
                          {(d.line_items || []).length} lines
                          {d.document_date ? ` · ${d.document_date}` : ""}
                        </div>
                      </div>
                      {pullingDid === d.id && (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Iter 412 (Dirk, 2026-02-28) — Overall defaults strip. Set
          delivery method, carrier, ready date, ETA ONCE at tracker
          level, then click Apply to push into every line (blanks only
          by default, tick "Overwrite" to force). Auto ETA = ready +2
          days is derived server-side when method is Collection. */}
      <OverallDefaults tracker={tracker} tid={tid} projects={projects} onSaved={load} />

      {/* Items table */}
      <div className="hv-card p-3">
        <div className="flex items-center gap-2 mb-2">
          <Package className="w-3.5 h-3.5 text-[#1FB6C1]" />
          <div className="text-sm font-bold">Lines ({items.length})</div>
        </div>
        {items.length === 0 ? (
          <div className="text-center text-xs text-[#a0aabf] py-6">
            No lines yet. Use &ldquo;Pull from Supplier Doc&rdquo; above to
            populate from an OCR&apos;d document.
          </div>
        ) : (
          <BundledLines
            items={items}
            defaultEta={tracker.default_expected_delivery_date}
            trackerDefaults={{
              delivery_method: tracker.delivery_method,
              carrier: tracker.default_carrier,
              carrier_reference: tracker.default_carrier_reference,
              ready_for_collection_at: tracker.default_ready_for_collection_at,
              project_id: tracker.project_id,
            }}
            projects={projects}
            onPatch={patchItem}
            onDelete={deleteItem}
            onReload={load}
            highlight={highlightItemId}
          />
        )}
      </div>

      {/* GRV history */}
      {grvs.length > 0 && (
        <div className="mt-3 hv-card p-3" data-testid="procurement-grv-history">
          <div className="flex items-center gap-2 mb-2">
            <ClipboardCheck className="w-3.5 h-3.5 text-emerald-400" />
            <div className="text-sm font-bold">
              Goods Received ({grvs.length})
            </div>
          </div>
          <div className="space-y-1.5">
            {grvs.map((g) => (
              <GrvRow
                key={g.id}
                g={g}
                onDelete={async () => {
                  if (!window.confirm("Reverse this GRV? qty_delivered and inventory on-hand will be decremented.")) return;
                  try {
                    await api.delete(`/procurement/grvs/${g.id}`);
                    toast.success("GRV reversed.");
                    await load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Reverse failed.");
                  }
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Source docs */}
      {(tracker.source_supplier_doc_ids || []).length > 0 && (
        <div className="mt-3 hv-card p-3">
          <div className="text-[11px] uppercase tracking-wider text-[#647087] mb-2">
            Source POs
          </div>
          <div className="flex flex-wrap gap-1">
            {tracker.source_supplier_doc_ids.map((sid) => (
              <Link
                key={sid}
                to={`/suppliers/${tracker.supplier_id}`}
                className="text-[10px] px-1.5 py-0.5 rounded border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20"
                data-testid={`procurement-source-doc-${sid}`}
              >
                {sid.slice(0, 12)}
              </Link>
            ))}
          </div>
        </div>
      )}

      {showRescan && (
        <RescanDialog tracker={tracker} docs={supplierDocs} onClose={() => setShowRescan(false)} onApplied={load} />
      )}

      {/* Receive stock (GRV) modal */}
      {showGrvModal && (
        <GrvModal
          tracker={tracker}
          items={items}
          onClose={() => setShowGrvModal(false)}
          onSaved={async () => {
            setShowGrvModal(false);
            await load();
          }}
        />
      )}
    </div>
  );
}


/**
 * BundledLines — Iter 411 (Dirk, 2026-02-28).
 *
 * Groups procurement items by (delivery_method, carrier, eta) into
 * visual sections so a 40-line PO with mixed delivery + collection
 * splits reads as "12 lines delivered by Fastway ETA 5 Mar" + "6
 * lines for collection by client ETA 8 Mar" instead of an
 * indistinguishable long list.
 *
 * Items with no configured delivery bundle group under an "Unassigned"
 * section at the top so operators immediately see what needs setup.
 */
function BundledLines({ items, defaultEta, trackerDefaults, projects, onPatch, onDelete, onReload, highlight }) {
  const groups = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const dm = it.delivery_method || "";
      const carrier = it.carrier || "";
      const eta = it.expected_delivery_date || "";
      const key = dm || carrier || eta ? `${dm}|${carrier}|${eta}` : "__unassigned";
      if (!map.has(key)) map.set(key, { key, delivery_method: dm, carrier, eta, rows: [] });
      map.get(key).rows.push(it);
    }
    const arr = Array.from(map.values());
    // Unassigned bucket first, then chronological by ETA.
    arr.sort((a, b) => {
      if (a.key === "__unassigned") return -1;
      if (b.key === "__unassigned") return 1;
      return (a.eta || "").localeCompare(b.eta || "");
    });
    return arr;
  }, [items]);

  return (
    <div className="space-y-3">
      {groups.map((g) => (
        <div
          key={g.key}
          className="border border-white/5 rounded"
          data-testid={`procurement-bundle-${g.key}`}
        >
          <div className={`px-2 py-1.5 text-[10px] uppercase tracking-wider font-bold ${g.key === "__unassigned" ? "bg-amber-500/10 text-amber-200" : "bg-white/[0.03] text-[#a0aabf]"}`}>
            {g.key === "__unassigned"
              ? <>Unassigned · {g.rows.length} line{g.rows.length === 1 ? "" : "s"} — set delivery/collection details below</>
              : <>{g.delivery_method || "—"}{g.carrier ? ` · ${g.carrier}` : ""}{g.eta ? ` · ETA ${g.eta}` : ""} · {g.rows.length} line{g.rows.length === 1 ? "" : "s"}</>}
          </div>
          <div className="md:overflow-x-auto">
            <table className="w-full text-xs table-fixed md:table-auto">
              <thead className="text-[10px] uppercase tracking-wider text-[#647087] border-b border-white/5">
                <tr>
                  <th className="text-left py-1.5 px-2">Item</th>
                  <th className="text-right py-1.5 px-2 hidden md:table-cell">Ordered</th>
                  <th className="text-right py-1.5 px-2 hidden md:table-cell">Delivered</th>
                  <th className="text-right py-1.5 px-2 hidden md:table-cell">Outstanding</th>
                  <th className="text-left py-1.5 px-2 hidden md:table-cell">ETA</th>
                  <th className="text-right py-1.5 px-2 w-16 md:w-auto"></th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map((it) => (
                  <ItemRow
                    key={it.id}
                    it={it}
                    onPatch={(patch) => onPatch(it.id, patch)}
                    onDelete={() => onDelete(it.id)}
                    defaultEta={defaultEta}
                    trackerDefaults={trackerDefaults}
                    projects={projects}
                    highlight={highlight}
                    siblings={items}
                    onReload={onReload}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}


function OverallDefaults({ tracker, tid, projects, onSaved }) {
  // Iter 412 (Dirk, 2026-02-28) — Overall defaults strip. Mirrors the
  // Picking-slip pattern of "set once, apply to all lines". User only
  // has to touch the outliers.
  // Iter 425.d (Dirk, 2026-03-01) — Adds tracker-level Project field +
  // "Apply project to all lines" button so the whole PO can be
  // allocated to a project in one action (with per-line reassignment
  // available inside each line's expanded editor).
  const [method, setMethod] = useState(tracker.delivery_method || "");
  const [carrier, setCarrier] = useState(tracker.default_carrier || "");
  const [ref, setRef] = useState(tracker.default_carrier_reference || "");
  const [ready, setReady] = useState(tracker.default_ready_for_collection_at || "");
  const [eta, setEta] = useState(tracker.default_expected_delivery_date || "");
  const [projectId, setProjectId] = useState(tracker.project_id || "");
  const [overwrite, setOverwrite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyingProject, setApplyingProject] = useState(false);

  // Auto-suggest ETA when method is collection + ready date is set.
  const autoEta = (() => {
    if (method !== "collection" || !ready) return null;
    try {
      const d = new Date(`${ready}T00:00:00`);
      d.setDate(d.getDate() + 2);
      return d.toISOString().slice(0, 10);
    } catch { return null; }
  })();

  const dirty =
    (method || null) !== (tracker.delivery_method || null) ||
    (carrier || null) !== (tracker.default_carrier || null) ||
    (ref || null) !== (tracker.default_carrier_reference || null) ||
    (ready || null) !== (tracker.default_ready_for_collection_at || null) ||
    (eta || null) !== (tracker.default_expected_delivery_date || null) ||
    (projectId || null) !== (tracker.project_id || null);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        delivery_method: method || null,
        default_carrier: carrier || null,
        default_carrier_reference: ref || null,
        default_ready_for_collection_at: ready || null,
        default_expected_delivery_date: eta || autoEta || null,
        // Iter 425.d — persist tracker-level project alongside the
        // rest of the overall defaults.
        project_id: projectId || null,
      };
      await api.patch(`/procurement/trackers/${tid}`, patch);
      toast.success("Overall defaults saved.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const applyToAll = async () => {
    if (dirty) {
      const ok = window.confirm("Unsaved changes to the overall defaults. Save first?");
      if (ok) await save();
      else return;
    }
    setApplying(true);
    try {
      const { data } = await api.post(
        `/procurement/trackers/${tid}/apply-defaults`,
        { overwrite },
      );
      const n = data?.items_updated ?? 0;
      if (n === 0) {
        toast(`No line changes${overwrite ? "" : " (all fields already set)"}.`);
      } else {
        toast.success(`${n} line${n === 1 ? "" : "s"} updated.`);
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed.");
    } finally {
      setApplying(false);
    }
  };

  const applyProject = async () => {
    // Iter 425.d — Push the tracker-level project down onto every
    // line. Respects the `overwrite` toggle so lines already assigned
    // to a different project only get steamrolled when the user opts in.
    setApplyingProject(true);
    try {
      const { data } = await api.post(
        `/procurement/trackers/${tid}/apply-project`,
        { project_id: projectId || "", overwrite },
      );
      const n = data?.items_updated ?? 0;
      if (n === 0) {
        toast(overwrite
          ? "No line project changes (already matching)."
          : "No line project changes (all lines already assigned).");
      } else {
        toast.success(
          projectId
            ? `${n} line${n === 1 ? "" : "s"} linked to project.`
            : `${n} line${n === 1 ? "" : "s"} cleared.`,
        );
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply project failed.");
    } finally {
      setApplyingProject(false);
    }
  };

  return (
    <div className="hv-card p-3 mb-4" data-testid="procurement-overall-defaults">
      <div className="flex items-center gap-2 mb-2">
        <Truck className="w-3.5 h-3.5 text-[#1FB6C1]" />
        <div className="text-sm font-bold">Overall delivery / collection</div>
        <div className="text-[10px] text-[#647087] hidden md:block">
          Set once here → &ldquo;Apply to lines&rdquo; pre-fills every row (edit outliers below).
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
          Order method
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="hv-input text-xs"
            data-testid="procurement-overall-method"
          >
            <option value="">— unset —</option>
            <option value="delivery">Delivery</option>
            <option value="collection">Collection</option>
          </select>
        </label>
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
          Carrier
          <input
            type="text"
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
            placeholder="e.g. Fastway"
            className="hv-input text-xs"
            data-testid="procurement-overall-carrier"
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
          Waybill
          <input
            type="text"
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder="waybill / ref"
            className="hv-input text-xs"
            data-testid="procurement-overall-ref"
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
          Ready for collection
          <input
            type="date"
            value={ready}
            onChange={(e) => setReady(e.target.value)}
            className="hv-input text-xs"
            data-testid="procurement-overall-ready"
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1 col-span-2 md:col-span-1">
          ETA
          <input
            type="date"
            value={eta}
            onChange={(e) => setEta(e.target.value)}
            placeholder={autoEta || ""}
            className="hv-input text-xs"
            data-testid="procurement-overall-eta"
          />
          {method === "collection" && autoEta && !eta && (
            <span className="text-[9px] text-[#647087] normal-case font-normal">
              Auto = {autoEta} (ready +2 days)
            </span>
          )}
        </label>
        {/* Iter 425.d — Overall project allocation. Selection stays
            on the tracker until the user clicks "Apply project to
            lines" to push it down onto every non-overridden line. */}
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1 col-span-2 md:col-span-1">
          Project
          <ProjectSearchSelect
            value={projectId}
            projects={projects}
            onChange={setProjectId}
            testid="procurement-overall-project"
          />
        </label>
      </div>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          className="hv-btn hv-btn-primary text-xs"
          data-testid="procurement-overall-save"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          Save overall
        </button>
        <button
          type="button"
          onClick={applyToAll}
          disabled={applying}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="procurement-overall-apply"
          title="Apply the overall values to every line (blanks only by default)."
        >
          {applying ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
          Apply to lines
        </button>
        {/* Iter 425.d — Push the tracker-level project onto every
            line. Separate action so ops can save-and-apply project
            without touching delivery defaults. */}
        <button
          type="button"
          onClick={applyProject}
          disabled={applyingProject}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="procurement-overall-apply-project"
          title="Push this project onto every line (respects overwrite toggle)."
        >
          {applyingProject ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
          Apply project to lines
        </button>
        <label className="text-[10px] text-[#a0aabf] inline-flex items-center gap-1.5 select-none">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => setOverwrite(e.target.checked)}
            data-testid="procurement-overall-overwrite"
          />
          Overwrite existing line values
        </label>
        {ready && (
          <span className="ml-auto text-[10px] text-amber-300/80 inline-flex items-center gap-1"
                title="Reminders auto-created for every unique ready date across this tracker.">
            <AlertTriangle className="w-3 h-3" />
            Reminder 1 day before ({ready ? new Date(new Date(`${ready}T00:00:00`).setDate(new Date(`${ready}T00:00:00`).getDate() - 1)).toISOString().slice(0, 10) : ""})
          </span>
        )}
      </div>
    </div>
  );
}


function MatchBadge({ it }) {
  // Iter 411 (Dirk, 2026-02-28) — Row-level match confidence indicator.
  // Green ✓ when the row is linked to inventory (safe to receive & bump
  // on_hand). Amber ⚠ when the row is not yet matched — operator can
  // still expand the row and use the Link button to fix. Mirrors the
  // matched / unmatched badges shown on picking rows.
  const linkedInv = !!it.inventory_id;
  const anyLink = linkedInv
    || !!it.linked_picking_item_id
    || !!it.linked_sales_order_id
    || !!it.linked_jobcard_id;
  if (linkedInv) {
    return (
      <span
        className="text-[9px] font-mono uppercase tracking-wider border px-1.5 py-[1px] rounded bg-emerald-500/10 text-emerald-300 border-emerald-500/30 inline-flex items-center gap-0.5 shrink-0"
        data-testid={`procurement-item-match-${it.id}`}
        title="Matched to inventory — safe to receive."
      >
        <CheckCircle2 className="w-2.5 h-2.5" /> matched
      </span>
    );
  }
  if (anyLink) {
    return (
      <span
        className="text-[9px] font-mono uppercase tracking-wider border px-1.5 py-[1px] rounded bg-cyan-500/10 text-cyan-300 border-cyan-500/30 inline-flex items-center gap-0.5 shrink-0"
        data-testid={`procurement-item-match-${it.id}`}
        title="Linked to a picking/SO/JC. Add an inventory match for stock accounting."
      >
        <LinkIcon className="w-2.5 h-2.5" /> linked
      </span>
    );
  }
  return (
    <span
      className="text-[9px] font-mono uppercase tracking-wider border px-1.5 py-[1px] rounded bg-amber-500/15 text-amber-200 border-amber-400/40 inline-flex items-center gap-0.5 shrink-0"
      data-testid={`procurement-item-match-${it.id}`}
      title="Not matched. Expand row and click Link to attach inventory / picking / SO / JC."
    >
      <AlertTriangle className="w-2.5 h-2.5" /> unmatched
    </span>
  );
}


function ItemRow({ it, onPatch, onDelete, defaultEta, trackerDefaults, projects, highlight, siblings, onReload }) {
  const [editing, setEditing] = useState(null); // "ordered" | "delivered" | "eta" | null
  const [draft, setDraft] = useState("");
  // Iter 411 (Dirk, 2026-02-28) — expandable delivery/collection editor
  // mirroring the picking-row expander pattern. Click the row → panel
  // opens with delivery method, carrier, carrier reference, notes.
  const [expanded, setExpanded] = useState(false);
  // Iter 426.p10 — auto-scroll + flash when arrived via
  // /procurement/{tid}?highlight={item_id}. Also auto-expands the
  // detail panel so the per-row project_id editor is one click
  // away for "unlink from this project" workflows.
  const _rowRef = useRef(null);
  useEffect(() => {
    if (highlight && it.id === highlight && _rowRef.current) {
      try {
        _rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch (_e) {}
      setExpanded(true);
    }
  }, [highlight, it.id]);

  function beginEdit(field, currentVal) {
    setEditing(field);
    setDraft(currentVal == null ? "" : String(currentVal));
  }
  function cancelEdit() {
    setEditing(null);
    setDraft("");
  }
  async function commit() {
    if (editing === null) return;
    if (editing === "eta") {
      await onPatch({ expected_delivery_date: draft || null });
    } else {
      const n = Number(draft);
      if (Number.isNaN(n) || n < 0) {
        toast.error("Enter a non-negative number.");
        return;
      }
      const key = editing === "ordered" ? "qty_ordered" : "qty_delivered";
      await onPatch({ [key]: n });
    }
    cancelEdit();
  }

  const outstanding = Math.max(
    0,
    Number(it.qty_ordered || 0) - Number(it.qty_delivered || 0)
  );
  const done = outstanding <= 0 && Number(it.qty_ordered || 0) > 0;

  return (
    <>
    <tr
      ref={_rowRef}
      className={`border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] cursor-pointer ${
        highlight && it.id === highlight ? "bg-cyan-500/15 ring-2 ring-cyan-400/60" : ""
      }`}
      data-testid={`procurement-item-row-${it.id}`}
      onClick={(e) => {
        // Ignore clicks on interactive children (buttons, inputs).
        if (e.target.closest("button, input, select, textarea, a")) return;
        setExpanded((v) => !v);
      }}
    >
      <td className="py-1.5 px-2">
        <div className="flex items-start gap-1.5 min-w-0">
          {done ? (
            <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0 mt-1" />
          ) : outstanding > 0 ? (
            <AlertTriangle className="w-3 h-3 text-amber-400/60 shrink-0 mt-1" />
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="font-bold text-[#dde3ec] flex items-center gap-1.5 flex-wrap">
              <span className="break-words" style={{ wordBreak: "break-word" }}>{it.name || it.sku || "—"}</span>
              {/* Iter 411 (Dirk, 2026-02-28) — Row-level match badge.
                  Mirrors the picking-slip inventory match badge so
                  operators instantly see which lines still need to be
                  linked to inventory / SO / JC before receiving stock. */}
              <MatchBadge it={it} onPatch={onPatch} />
              <EditLineButton item={it} url={`/procurement/items/${it.id}`} onDone={onReload} testPrefix="procurement-item" />
            </div>
            {(it.sku || it.source_doc_number) && (
              <div className="text-[10px] text-[#647087] truncate">
                {it.sku ? `SKU ${it.sku}` : ""}
                {it.sku && it.source_doc_number ? " · " : ""}
                {it.source_doc_number ? `From ${it.source_doc_number}` : ""}
              </div>
            )}
            <MergedLinesPanel item={it} baseUrl={`/procurement/items/${it.id}`} onDone={onReload} testPrefix="procurement-item" />
            {(it.linked_picking_item_id || it.inventory_id
              || it.linked_sales_order_id || it.linked_jobcard_id) && (
              <div className="text-[10px] mt-0.5 flex items-center gap-1 flex-wrap">
                {it.linked_picking_item_id && (
                  <span className="px-1 py-[1px] rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/30"
                        data-testid={`procurement-item-link-picking-${it.id}`}>
                    <LinkIcon className="w-2.5 h-2.5 inline mr-0.5" />
                    picking#{String(it.linked_picking_item_id).slice(0, 8)}
                  </span>
                )}
                {it.inventory_id && (
                  <span className="px-1 py-[1px] rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"
                        data-testid={`procurement-item-link-inv-${it.id}`}>
                    inv#{String(it.inventory_id).slice(0, 8)}
                  </span>
                )}
                {it.linked_sales_order_id && (
                  <span className="px-1 py-[1px] rounded bg-violet-500/10 text-violet-300 border border-violet-500/30"
                        data-testid={`procurement-item-link-so-${it.id}`}>
                    SO#{String(it.linked_sales_order_id).slice(0, 8)}
                  </span>
                )}
                {it.linked_jobcard_id && (
                  <span className="px-1 py-[1px] rounded bg-amber-500/10 text-amber-300 border border-amber-500/30"
                        data-testid={`procurement-item-link-jc-${it.id}`}>
                    JC#{String(it.linked_jobcard_id).slice(0, 8)}
                  </span>
                )}
              </div>
            )}
            {/* Iter 411 mobile — Ordered / Outstanding compact chips
                since those columns are hidden on < md. */}
            <div className="md:hidden mt-1 flex items-center gap-1 text-[10px] font-mono text-[#a0aabf]">
              <span title="Ordered">ord {fmtQty(it.qty_ordered)}</span>
              <span className="text-[#647087]">·</span>
              <span title="Delivered" className="text-emerald-300/80">dlv {fmtQty(it.qty_delivered)}</span>
              {outstanding > 0 && (
                <>
                  <span className="text-[#647087]">·</span>
                  <span title="Outstanding" className="text-amber-300">out {fmtQty(outstanding)}</span>
                </>
              )}
              {(it.expected_delivery_date || defaultEta) && (
                <>
                  <span className="text-[#647087]">·</span>
                  <span title="ETA">{it.expected_delivery_date || `(${defaultEta})`}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="py-1.5 px-2 text-right font-mono hidden md:table-cell">
        {editing === "ordered" ? (
          <EditCell
            value={draft}
            setValue={setDraft}
            onCommit={commit}
            onCancel={cancelEdit}
          />
        ) : (
          <button
            onClick={() => beginEdit("ordered", it.qty_ordered)}
            className="hover:text-[#1FB6C1]"
            data-testid={`procurement-item-ordered-${it.id}`}
          >
            {fmtQty(it.qty_ordered)}
          </button>
        )}
      </td>
      <td className="py-1.5 px-2 text-right font-mono hidden md:table-cell">
        {editing === "delivered" ? (
          <EditCell
            value={draft}
            setValue={setDraft}
            onCommit={commit}
            onCancel={cancelEdit}
          />
        ) : (
          <button
            onClick={() => beginEdit("delivered", it.qty_delivered)}
            className="hover:text-emerald-300"
            data-testid={`procurement-item-delivered-${it.id}`}
          >
            {fmtQty(it.qty_delivered)}
          </button>
        )}
      </td>
      <td className="py-1.5 px-2 text-right font-mono text-[#a0aabf] hidden md:table-cell">
        {fmtQty(outstanding)}
      </td>
      <td className="py-1.5 px-2 hidden md:table-cell">
        {editing === "eta" ? (
          <div className="flex items-center gap-1">
            <input
              type="date"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="hv-input text-[11px] py-0.5"
              autoFocus
            />
            <button
              onClick={commit}
              className="hv-btn hv-btn-primary text-[10px] py-0.5 px-1.5"
            >
              OK
            </button>
            <button
              onClick={cancelEdit}
              className="hv-btn hv-btn-ghost text-[10px] py-0.5 px-1.5"
            >
              ×
            </button>
          </div>
        ) : (
          <button
            onClick={() => beginEdit("eta", it.expected_delivery_date)}
            className="text-[11px] text-[#a0aabf] hover:text-[#1FB6C1]"
            data-testid={`procurement-item-eta-${it.id}`}
          >
            {it.expected_delivery_date ||
              (defaultEta ? `(${defaultEta})` : "—")}
          </button>
        )}
      </td>
      <td className="py-1.5 px-2 text-right">
        <div className="flex items-center gap-1 justify-end">
          <MergeIntoButton item={it} siblings={siblings} onDone={onReload} />
          <LinkerButton item={it} onPatch={onPatch} />
          <button
            onClick={onDelete}
            className="text-rose-300/60 hover:text-rose-300"
            title="Remove"
            data-testid={`procurement-item-delete-${it.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </td>
    </tr>
    {expanded && (
      <tr
        className="border-b border-white/5 bg-black/30"
        data-testid={`procurement-item-expand-${it.id}`}
      >
        <td colSpan={2} className="p-3 md:hidden">
          <ExpandedItemFields
            it={it}
            onPatch={onPatch}
            trackerDefaults={trackerDefaults}
            projects={projects}
          />
        </td>
        <td colSpan={6} className="p-3 hidden md:table-cell">
          <ExpandedItemFields
            it={it}
            onPatch={onPatch}
            trackerDefaults={trackerDefaults}
            projects={projects}
          />
        </td>
      </tr>
    )}
    </>
  );
}

function ExpandedItemFields({ it, onPatch, trackerDefaults, projects }) {
  // Iter 411 (Dirk, 2026-02-28) — Extracted expanded-row content so both
  // the mobile (colSpan=2) and desktop (colSpan=6) rows share the same
  // form. Includes the new "Ready for collection" date field.
  // Iter 425.d (Dirk, 2026-03-01) — Also carries the per-line project
  // selector so ops can reassign a single line to a different project
  // than the tracker's overall allocation (or clear it).
  const method = it.delivery_method || trackerDefaults?.delivery_method || "";
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
      {/* Ordered / Delivered / ETA quick-edit — visible on mobile since
          those columns are hidden in the table row. */}
      <div className="md:hidden grid grid-cols-3 gap-2 col-span-1 sm:col-span-2">
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
          Ordered
          <input
            type="number" step="any"
            defaultValue={it.qty_ordered ?? 0}
            onBlur={(e) => {
              const n = Number(e.target.value);
              if (!Number.isNaN(n) && n !== Number(it.qty_ordered || 0)) onPatch({ qty_ordered: n });
            }}
            className="hv-input text-xs mt-0 text-right font-mono"
            data-testid={`procurement-item-ordered-mob-${it.id}`}
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
          Delivered
          <input
            type="number" step="any"
            defaultValue={it.qty_delivered ?? 0}
            onBlur={(e) => {
              const n = Number(e.target.value);
              if (!Number.isNaN(n) && n !== Number(it.qty_delivered || 0)) onPatch({ qty_delivered: n });
            }}
            className="hv-input text-xs mt-0 text-right font-mono"
            data-testid={`procurement-item-delivered-mob-${it.id}`}
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
          &nbsp;
          <div className="text-[10px] font-mono text-[#a0aabf] py-1 px-2 rounded bg-white/[0.02]">
            Outstanding
            <div className="text-sm text-amber-300">{Math.max(0, Number(it.qty_ordered || 0) - Number(it.qty_delivered || 0))}</div>
          </div>
        </label>
      </div>

      {/* Delivery method */}
      <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
        Delivery method
        <select
          value={it.delivery_method || ""}
          onChange={(e) => onPatch({ delivery_method: e.target.value || null })}
          className="hv-input text-xs mt-0"
          data-testid={`procurement-item-delivery-method-${it.id}`}
        >
          <option value="">— {trackerDefaults?.delivery_method ? `inherit (${trackerDefaults.delivery_method})` : "unset"} —</option>
          <option value="delivery">Delivery</option>
          <option value="collection">Collection</option>
        </select>
      </label>
      {/* Carrier */}
      <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
        Carrier
        <input
          type="text"
          defaultValue={it.carrier || ""}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v !== (it.carrier || "")) onPatch({ carrier: v || null });
          }}
          placeholder={trackerDefaults?.carrier ? `inherit (${trackerDefaults.carrier})` : "e.g. Fastway"}
          className="hv-input text-xs mt-0"
          data-testid={`procurement-item-carrier-${it.id}`}
        />
      </label>
      {/* Carrier reference (waybill / tracking ID) */}
      <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
        Waybill / tracking
        <input
          type="text"
          defaultValue={it.carrier_reference || ""}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v !== (it.carrier_reference || "")) onPatch({ carrier_reference: v || null });
          }}
          placeholder="tracking id / waybill"
          className="hv-input text-xs mt-0"
          data-testid={`procurement-item-cref-${it.id}`}
        />
      </label>
      {/* Iter 412 (Dirk, 2026-02-28) — Ready-for-collection date. Only
          meaningful when delivery_method resolves to "collection"; the
          UI still exposes it always so operators can front-load it
          before setting the method. Reminders auto-generated one day
          prior via the backend. */}
      <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
        Ready for collection
        <input
          type="date"
          defaultValue={it.ready_for_collection_at || ""}
          onBlur={(e) => {
            const v = e.target.value;
            if (v !== (it.ready_for_collection_at || "")) onPatch({ ready_for_collection_at: v || null });
          }}
          className="hv-input text-xs mt-0"
          data-testid={`procurement-item-ready-${it.id}`}
        />
      </label>
      {/* ETA override */}
      <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
        ETA (this line)
        <input
          type="date"
          defaultValue={it.expected_delivery_date || ""}
          onBlur={(e) => {
            const v = e.target.value;
            if (v !== (it.expected_delivery_date || "")) onPatch({ expected_delivery_date: v || null });
          }}
          className="hv-input text-xs mt-0"
          data-testid={`procurement-item-eta-input-${it.id}`}
        />
        {method === "collection" && it.ready_for_collection_at && !it.expected_delivery_date && (
          <span className="text-[9px] text-[#647087] normal-case font-normal">
            Auto-derives to ready +2 days on save.
          </span>
        )}
      </label>
      {/* Iter 425.d (Dirk, 2026-03-01) → Iter 427.p3 — Per-line project
          selector. Blank keeps the line inheriting the tracker's own
          project. Picking "— none —" while the tracker itself is bound
          to a project stamps the sentinel `"__none__"` so the backend
          spend aggregator drops this line from that project's finances
          entirely (Dirk: "If project says none it is supposed to be
          removed from financial!"). Selecting a real project overrides
          the tracker's project for this line only. */}
      <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
        Project (this line)
        <ProjectSearchSelect
          value={it.project_id === "__none__" ? "" : (it.project_id || "")}
          projects={projects}
          onChange={(v) => onPatch({
            project_id: v
              ? v
              : (trackerDefaults?.project_id ? "__none__" : ""),
          })}
          testid={`procurement-item-project-${it.id}`}
        />
        {it.project_id === "__none__" && trackerDefaults?.project_id && (
          <span className="text-[9px] normal-case font-normal text-amber-300">
            Excluded from the tracker's project spend.
          </span>
        )}
      </label>
      {/* Iter 425.h — Per-line unit cost. Blank = 0 (falls back to
          inventory avg_cost at spend-breakdown time). Fixes legacy
          pull-from-supplier-doc rows that never captured cost. */}
      <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
        Unit cost
        <input
          type="number"
          min="0"
          step="0.01"
          value={it.unit_cost ?? ""}
          onChange={(e) => onPatch({
            unit_cost: e.target.value === "" ? 0 : Number(e.target.value),
          })}
          placeholder="e.g. 14646.00"
          className="hv-input text-xs"
          data-testid={`procurement-item-unit-cost-${it.id}`}
        />
      </label>
      {/* Notes — full width */}
      <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1 sm:col-span-2 md:col-span-4">
        Notes for this line
        <BlurTextarea
          rows={2}
          value={it.notes || ""}
          onSave={(v) => { if (v !== (it.notes || "")) onPatch({ notes: v }); }}
          placeholder="Anything the picking / receiving team needs to know about this line"
          className="hv-input text-xs mt-0 resize-y w-full"
          testid={`procurement-item-notes-${it.id}`}
          voiceContext="general"
        />
      </label>
    </div>
  );
}


function LinkerButton({ item, onPatch }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("picking"); // "picking" | "inv" | "so" | "jc"
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  // Clear stale results the moment the user switches tabs so the
  // previous tab's rows don't linger while the new fetch is in-flight.
  useEffect(() => { setResults([]); }, [tab]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (!term) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setBusy(true);
      try {
        const paths = {
          picking: "picking-items",
          inv: "inventory",
          so: "sales-orders",
          jc: "jobcards",
        };
        const url = `/procurement/search/${paths[tab]}?q=${encodeURIComponent(term)}&limit=15`;
        const { data } = await api.get(url);
        setResults(data?.items || []);
      } catch {
        setResults([]);
      } finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [q, tab, open]);

  async function link(target) {
    try {
      if (tab === "picking") {
        await onPatch({
          linked_picking_item_id: target.id,
          linked_picking_list_id: target.list_id,
        });
        toast.success(`Linked to ${target.name}`);
      } else if (tab === "inv") {
        await onPatch({ inventory_id: target.id });
        toast.success(`Linked to inventory: ${target.name}`);
      } else if (tab === "so") {
        await onPatch({ linked_sales_order_id: target.id });
        toast.success(`Linked to SO ${target.short_id || target.document_number || target.title || ""}`);
      } else if (tab === "jc") {
        await onPatch({ linked_jobcard_id: target.id });
        toast.success(`Linked to JC ${target.short_id || target.title || ""}`);
      }
      setOpen(false);
      setQ("");
      setResults([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed.");
    }
  }

  async function unlink(field) {
    try {
      await onPatch({ [field]: null });
      toast.success("Unlinked.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlink failed.");
    }
  }

  const hasLink = !!item.linked_picking_item_id || !!item.inventory_id
    || !!item.linked_sales_order_id || !!item.linked_jobcard_id;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`p-0.5 rounded hover:bg-white/5 ${
          hasLink ? "text-cyan-300" : "text-[#647087] hover:text-[#dde3ec]"
        }`}
        title={hasLink ? "Manage links" : "Link to picking / inventory / SO / JC"}
        data-testid={`procurement-item-link-btn-${item.id}`}
      >
        <LinkIcon className="w-3 h-3" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-30 w-80 hv-card p-2 space-y-1.5"
          onClick={(e) => e.stopPropagation()}
          data-testid={`procurement-item-link-menu-${item.id}`}
        >
          <div className="flex items-center gap-1 border-b border-white/5 pb-1 flex-wrap">
            {[
              { v: "picking", l: "Picking", tone: "cyan" },
              { v: "inv", l: "Inventory", tone: "emerald" },
              { v: "so", l: "SO", tone: "violet" },
              { v: "jc", l: "JC", tone: "amber" },
            ].map((t) => {
              const active = tab === t.v;
              const cls = active
                ? {
                    cyan: "bg-cyan-500/15 text-cyan-300 border border-cyan-500/40",
                    emerald: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40",
                    violet: "bg-violet-500/15 text-violet-300 border border-violet-500/40",
                    amber: "bg-amber-500/15 text-amber-300 border border-amber-500/40",
                  }[t.tone]
                : "text-[#a0aabf] hover:text-white";
              return (
                <button
                  key={t.v}
                  onClick={() => setTab(t.v)}
                  className={`text-[10px] px-1.5 py-0.5 rounded ${cls}`}
                  data-testid={`procurement-linker-tab-${t.v}-${item.id}`}
                >
                  {t.l}
                </button>
              );
            })}
            <div className="flex-1" />
            <button
              onClick={() => setOpen(false)}
              className="text-[10px] text-[#647087] hover:text-white"
            >
              <X className="w-3 h-3" />
            </button>
          </div>

          {/* Current links summary */}
          {hasLink && (
            <div className="space-y-0.5 pb-1 border-b border-white/5">
              {item.linked_picking_item_id && (
                <div className="flex items-center gap-1 text-[10px]">
                  <span className="text-cyan-300">picking</span>
                  <span className="text-[#a0aabf] flex-1 truncate">
                    #{String(item.linked_picking_item_id).slice(0, 8)}
                  </span>
                  <button
                    onClick={() => unlink("linked_picking_item_id")}
                    className="text-rose-300/70 hover:text-rose-300"
                    data-testid={`procurement-item-unlink-picking-${item.id}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              )}
              {item.inventory_id && (
                <div className="flex items-center gap-1 text-[10px]">
                  <span className="text-emerald-300">inventory</span>
                  <span className="text-[#a0aabf] flex-1 truncate">
                    #{String(item.inventory_id).slice(0, 8)}
                  </span>
                  <button
                    onClick={() => unlink("inventory_id")}
                    className="text-rose-300/70 hover:text-rose-300"
                    data-testid={`procurement-item-unlink-inv-${item.id}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              )}
              {item.linked_sales_order_id && (
                <div className="flex items-center gap-1 text-[10px]">
                  <span className="text-violet-300">SO</span>
                  <span className="text-[#a0aabf] flex-1 truncate">
                    #{String(item.linked_sales_order_id).slice(0, 8)}
                  </span>
                  <button
                    onClick={() => unlink("linked_sales_order_id")}
                    className="text-rose-300/70 hover:text-rose-300"
                    data-testid={`procurement-item-unlink-so-${item.id}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              )}
              {item.linked_jobcard_id && (
                <div className="flex items-center gap-1 text-[10px]">
                  <span className="text-amber-300">JC</span>
                  <span className="text-[#a0aabf] flex-1 truncate">
                    #{String(item.linked_jobcard_id).slice(0, 8)}
                  </span>
                  <button
                    onClick={() => unlink("linked_jobcard_id")}
                    className="text-rose-300/70 hover:text-rose-300"
                    data-testid={`procurement-item-unlink-jc-${item.id}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Search */}
          <div className="flex items-center gap-1">
            <Search className="w-3 h-3 text-[#647087]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={{
                picking: "search open picking items…",
                inv: "search inventory…",
                so: "search sales orders…",
                jc: "search job cards…",
              }[tab]}
              className="hv-input text-[11px] py-0.5 flex-1"
              data-testid={`procurement-linker-input-${item.id}`}
            />
          </div>

          {/* Results */}
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {busy && (
              <div className="text-[10px] text-[#a0aabf] p-1 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> searching…
              </div>
            )}
            {!busy && q && results.length === 0 && (
              <div className="text-[10px] text-[#647087] p-1">No matches.</div>
            )}
            {results.map((r) => {
              const title = r.name || r.title || r.short_id || r.id.slice(0, 12);
              let subtitle = "";
              if (tab === "picking") {
                subtitle = [
                  r.sku ? `SKU ${r.sku}` : "",
                  r.list_name,
                  r.qty_required != null ? `req ${r.qty_required}` : "",
                ].filter(Boolean).join(" · ");
              } else if (tab === "inv") {
                const onh = r.on_hand ?? r.on_hand_qty;
                subtitle = [
                  r.sku ? `SKU ${r.sku}` : "",
                  onh != null ? `on-hand ${onh}${r.unit ? ` ${r.unit}` : ""}` : "",
                ].filter(Boolean).join(" · ");
              } else if (tab === "so") {
                subtitle = [
                  r.short_id || r.document_number,
                  r.customer_name,
                  r.status,
                ].filter(Boolean).join(" · ");
              } else if (tab === "jc") {
                subtitle = [
                  r.short_id,
                  r.customer_name,
                  r.status,
                ].filter(Boolean).join(" · ");
              }
              return (
                <button
                  key={r.id}
                  onClick={() => link(r)}
                  className="w-full text-left p-1 rounded hover:bg-white/5 border border-transparent hover:border-white/10"
                  data-testid={`procurement-linker-result-${r.id}`}
                >
                  <div className="text-[11px] font-bold text-[#dde3ec] truncate">
                    {title}
                  </div>
                  {subtitle && (
                    <div className="text-[9px] text-[#647087] truncate">
                      {subtitle}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function EditCell({ value, setValue, onCommit, onCancel }) {
  return (
    <div className="flex items-center gap-1 justify-end">
      <input
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onCommit();
          if (e.key === "Escape") onCancel();
        }}
        style={{ width: "5rem", flex: "0 0 5rem" }}
        className="hv-input text-[11px] py-0.5 text-right"
        autoFocus
      />
      <button
        onClick={onCommit}
        className="hv-btn hv-btn-primary text-[10px] py-0.5 px-1.5"
      >
        OK
      </button>
      <button
        onClick={onCancel}
        className="hv-btn hv-btn-ghost text-[10px] py-0.5 px-1.5"
      >
        ×
      </button>
    </div>
  );
}


function GrvRow({ g, onDelete }) {
  const totalQty = (g.lines || []).reduce(
    (a, l) => a + Number(l.qty_received || 0),
    0
  );
  const when = (g.received_at || g.created_at || "").slice(0, 10);
  return (
    <div
      className="p-2 rounded bg-black/20 border border-white/5"
      data-testid={`procurement-grv-row-${g.id}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <ClipboardCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-[#dde3ec] truncate">
            {g.delivery_note_number || `GRV · ${g.id.slice(0, 8)}`}
          </div>
          <div className="text-[10px] text-[#a0aabf]">
            {when} · {(g.lines || []).length} line
            {(g.lines || []).length === 1 ? "" : "s"} · {fmtQty(totalQty)} units
            {g.received_by_name ? ` · by ${g.received_by_name}` : ""}
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-rose-300/60 hover:text-rose-300 text-[10px] px-1.5 py-0.5 border border-rose-500/20 rounded hover:border-rose-500/50"
          title="Reverse this GRV"
          data-testid={`procurement-grv-delete-${g.id}`}
        >
          Reverse
        </button>
      </div>
      {g.notes && (
        <div className="text-[10px] text-[#647087] mt-1 pl-5">{g.notes}</div>
      )}
    </div>
  );
}

function GrvModal({ tracker, items, onClose, onSaved }) {
  const openItems = items.filter(
    (it) => Number(it.qty_ordered || 0) - Number(it.qty_delivered || 0) > 0
  );
  const [drafts, setDrafts] = useState(() =>
    Object.fromEntries(
      openItems.map((it) => [
        it.id,
        String(
          Math.max(
            0,
            Number(it.qty_ordered || 0) - Number(it.qty_delivered || 0)
          )
        ),
      ])
    )
  );
  const [dn, setDn] = useState("");
  const [receivedAt, setReceivedAt] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [receivedByName, setReceivedByName] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const totalUnits = Object.values(drafts).reduce(
    (a, v) => a + (Number(v) || 0),
    0
  );

  async function submit() {
    const lines = openItems
      .map((it) => ({
        procurement_item_id: it.id,
        qty_received: Number(drafts[it.id] || 0),
      }))
      .filter((l) => l.qty_received > 0);
    if (lines.length === 0) {
      toast.error("Enter at least one qty_received > 0.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/procurement/trackers/${tracker.id}/grvs`, {
        delivery_note_number: dn || null,
        received_at: receivedAt || null,
        received_by_name: receivedByName || null,
        notes: notes || null,
        lines,
      });
      toast.success(`GRV posted · ${lines.length} line${lines.length === 1 ? "" : "s"}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "GRV post failed.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="procurement-grv-modal"
    >
      <div
        className="hv-card w-full max-w-2xl p-4 space-y-3 my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <Truck className="w-4 h-4 text-emerald-400" />
          <div className="text-base font-bold flex-1">Receive stock (GRV)</div>
          <button
            onClick={onClose}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="procurement-grv-modal-close"
          >
            <X className="w-3 h-3" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#647087]">
              Delivery note #
            </label>
            <input
              value={dn}
              onChange={(e) => setDn(e.target.value)}
              className="hv-input text-xs w-full"
              placeholder="DN-4421"
              data-testid="procurement-grv-dn"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#647087]">
              Received on
            </label>
            <input
              type="date"
              value={receivedAt}
              onChange={(e) => setReceivedAt(e.target.value)}
              className="hv-input text-xs w-full"
              data-testid="procurement-grv-date"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#647087]">
              Received by
            </label>
            <input
              value={receivedByName}
              onChange={(e) => setReceivedByName(e.target.value)}
              className="hv-input text-xs w-full"
              placeholder="(you)"
              data-testid="procurement-grv-by"
            />
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#647087]">
            Notes
          </label>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="hv-input text-xs w-full"
            placeholder="Optional — damaged crate, backorder, etc."
            data-testid="procurement-grv-notes"
          />
        </div>

        <div className="border-t border-white/5 pt-2">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">
            Lines · qty received (defaults to outstanding)
          </div>
          {openItems.length === 0 ? (
            <div className="text-xs text-[#a0aabf] py-3 text-center">
              Nothing outstanding — all items fully received.
            </div>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {openItems.map((it) => {
                const outs =
                  Number(it.qty_ordered || 0) - Number(it.qty_delivered || 0);
                return (
                  <div
                    key={it.id}
                    className="flex items-center gap-2 p-1.5 rounded bg-black/20"
                    data-testid={`procurement-grv-line-${it.id}`}
                  >
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="text-xs font-bold truncate text-[#dde3ec]">
                        {it.name || it.sku || "—"}
                      </div>
                      <div className="text-[10px] text-[#a0aabf] truncate whitespace-nowrap">
                        outstanding {fmtQty(outs)} · ordered{" "}
                        {fmtQty(it.qty_ordered)}
                      </div>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={outs}
                      value={drafts[it.id] ?? ""}
                      onChange={(e) =>
                        setDrafts((d) => ({ ...d, [it.id]: e.target.value }))
                      }
                      style={{ width: "5rem", flex: "0 0 5rem" }}
                      className="hv-input text-[11px] py-0.5 text-right"
                      data-testid={`procurement-grv-line-input-${it.id}`}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-white/5">
          <div className="text-xs text-[#a0aabf]">
            Total to receive: <span className="font-mono text-white">{fmtQty(totalUnits)}</span>
          </div>
          <div className="flex-1" />
          <button
            onClick={onClose}
            className="hv-btn hv-btn-ghost text-xs"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            className="hv-btn hv-btn-primary text-xs"
            style={{ background: "#10B981", borderColor: "#10B981" }}
            disabled={saving || totalUnits <= 0}
            data-testid="procurement-grv-submit"
          >
            {saving ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" /> Posting…
              </>
            ) : (
              <>
                <ClipboardCheck className="w-3 h-3" /> Post GRV
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
